import React, { useEffect, useState, useCallback } from "react";
import { useDebouncedCallback } from "use-debounce";
import cn from "classnames";
import PropTypes from "prop-types";
import MUITextField from "@material-ui/core/TextField";
import FormControl from "@material-ui/core/FormControl";
import withStyles from "components/with-styles";
import { Field } from "formik";
import { formikPropTypes } from "components/common";
import FormFieldError from "components/form/form-field-error";
import styles from "./styles/form-textfield.css.js";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import { useDebouncingInputsContext } from "../debouncing-inputs-context";

const TextField = ({
  name,
  value,
  onChange,
  onBlur,
  classes,
  className,
  spaced = false,
  marginDense = false,
  inputProps,
  inputClasses = {},
  register = undefined,
  ...props
}) => {
  const [innerValue, setInnerValue] = useState("");
  const { debouncingValue } = useDebouncingInputsContext();

  useEffect(
    () => {
      if (value || value === 0) {
        setInnerValue(value);
      } else {
        setInnerValue("");
      }
    },
    [value],
  );

  const debouncedHandleOnChange = useDebouncedCallback(event => {
    onChange && onChange(event);
  }, debouncingValue);

  const handleOnChange = useCallback(
    event => {
      event.persist();

      const newValue = event.currentTarget.value;
      setInnerValue(newValue);
      debouncingValue === 0 ? onChange && onChange(event) : debouncedHandleOnChange(event);
    },
    [debouncedHandleOnChange, debouncingValue, onChange],
  );

  const matchesMobile = useMediaQuery("(max-width: 385px)");
  const propsDenseOnMobile = {
    inputProps: {
      ...inputProps,
    },
    classes: {
      ...inputClasses,
      input: classes.input_margin,
    },
  };

  const defaultProps = {
    inputProps: {
      ...inputProps,
    },
    classes: {
      ...inputClasses,
    },
  };

  return (
    <FormControl
      margin={spaced ? "normal" : "none"}
      fullWidth
      className={cn(className, classes.select_formcontrol, { [classes.select_top_margin]: spaced })}
    >
      {!!register ? <MUITextField
          {...register(name)}
          InputProps={marginDense && matchesMobile ? propsDenseOnMobile : defaultProps}
          name={name}
          value={innerValue === 0 || innerValue ? innerValue : ""}
          onBlur={onBlur}
          fullWidth
          {...props}
      /> : <MUITextField
          InputProps={marginDense && matchesMobile ? propsDenseOnMobile : defaultProps}
          name={name}
          value={innerValue === 0 || innerValue ? innerValue : ""}
          onChange={handleOnChange}
          onBlur={onBlur}
          fullWidth
          {...props}
      />}
      <FormFieldError name={name} />
    </FormControl>
  );
};

TextField.propTypes = {
  ...formikPropTypes,
  /**
   * Clase adicional del componente.
   */
  className: PropTypes.string,
  /**
   * Props del input (no incluye clases)
   */
  inputProps: PropTypes.object,
  /**
   * Clases del input de material ui
   */
  inputClasses: PropTypes.object,
  /**
   * Indica si utiliza los estilos spaced
   */
  spaced: PropTypes.bool,
};

export const StyledTextField = withStyles(styles)(TextField);

const ValidatedTextField = ({ field: { name, value, onChange, onBlur }, classes, ...props }) => {
  return <StyledTextField name={name} value={value} onChange={onChange} onBlur={onBlur} {...props} />;
};

ValidatedTextField.propTypes = {
  ...formikPropTypes,
};

const FormTextField = ({ name, onChange, value, onBlur, children, ...props }) => {
  const handleChange = ({ target: { value } }) => onChange(name)(value);
  return (
    <Field
      component={ValidatedTextField}
      field={{
        name,
        onChange: handleChange,
        value,
        onBlur,
      }}
      name={name}
      {...props}
    >
      {children}
    </Field>
  );
};

FormTextField.propTypes = {
  /**
   * Clase adicional del componente.
   */
  className: PropTypes.string,
  /**
   * Tipo de input (text, number...)
   */
  type: PropTypes.string,
  /**
   * Valor del input
   */
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  /**
   * Nombre del input
   */
  name: PropTypes.string,
  /**
   * Id del input
   */
  id: PropTypes.string,
  /**
   * Callback que se dispara con el cambio del input
   */
  onChange: PropTypes.func,
  /**
   * Callback que se dispara con el cambio de foco
   */
  onBlur: PropTypes.func,
  /**
   * Etiqueta del input
   */
  label: PropTypes.string,
  /**
   * Placeholder del input
   */
  placeholder: PropTypes.string,
  /**
   * Indica si el input está deshabilitado
   */
  disabled: PropTypes.bool,
  /**
   * Indica si utiliza los estilos spaced
   */
  spaced: PropTypes.bool,
  /**
   * Indica si utiliza los estilos marginDense para placeholders de inputs multilínea
   */
  marginDense: PropTypes.bool,
};

export default FormTextField;
